const cities = [
  { name: "Apache Junction", zips: "85119, 85120" },
  { name: "Gold Canyon", zips: "85118" },
  { name: "Queen Valley", zips: "85118" },
  { name: "Superior", zips: "85173" },
  { name: "Mesa (East)", zips: "85205, 85207, 85208, 85209" },
];

const benefits = [
  {
    icon: "ri-computer-line",
    title: "Start 100% Online",
    desc: "No office visit required to begin. Complete your order from your Apache Junction home, Gold Canyon resort, or RV park in minutes.",
  },
  {
    icon: "ri-truck-line",
    title: "Ship Your Documents",
    desc: "Use overnight shipping to send your documents directly to us — we handle every detail from there.",
  },
  {
    icon: "ri-shield-check-line",
    title: "Expert Document Review",
    desc: "Every application is reviewed by our team before submission to reduce errors and prevent delays.",
  },
  {
    icon: "ri-calendar-check-line",
    title: "Reserved Agency Access",
    desc: "We hold reserved submission slots at the regional passport agency — no public appointment needed.",
  },
];

export default function ApacheJunctionLocalAreaSection() {
  return (
    <section id="service-area" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Service Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Serving Apache Junction &amp; Pinal County
          </h2>
        </div>

        <div className="max-w-4xl mx-auto mb-16">
          <div className="bg-[#F7F8FA] rounded-lg p-8 md:p-10 border border-gray-200">
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              Apache Junction sits at the base of the Superstition Mountains, where Pinal County meets the eastern edge of the Phoenix metro. It is a city defined by seasonal rhythms — the winter influx of snowbirds and RV travelers from Canada and the northern United States, and the year-round presence of outdoor enthusiasts heading into Tonto National Forest, the Apache Trail, and the Superstition Wilderness. For a community where so many residents maintain dual residences across international borders, the passport is practically a utility item.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The most common passport urgency we see from Apache Junction clients is the snowbird discovery: a retiree packs up their Arizona RV in October, starts planning the winter in British Columbia or Ontario, and realizes their U.S. passport expired in 2021 when travel was suspended. Standard government processing through a Mesa or Chandler post office can take 2–4 months, which is simply not compatible with a departure booked for late October. Expedited courier service through Fast Passport Center closes that gap.
            </p>
            <p className="text-gray-600 text-base leading-[1.8] mb-5">
              The second wave comes from the Baja California and Rocky Point crowd. Apache Junction residents planning winter RV trips to Puerto Peñasco (Rocky Point) or extended Baja California coastal road trips frequently realize — sometimes at the Lukeville or Nogales border crossing — that their passport is close to expiration. While Mexico technically permits entry with limited remaining validity, many travelers prefer to renew before heading south to avoid complications at remote coastal areas far from consular services.
            </p>
            <p className="text-gray-600 text-base leading-[1.8]">
              For East Valley commuters and young families heading to Apache Junction from Mesa or Gilbert, expedited passport service is often needed for conference travel to Mexico City or Cancún, honeymoon cruises from Los Angeles or San Diego, or study-abroad programs at ASU. Fast Passport Center serves all of these scenarios with the same professional courier submission, document review, and predictable processing that Apache Junction residents deserve — without a 90-minute drive to downtown Phoenix.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="bg-[#1A365D] rounded-lg p-7 mb-8 text-white">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-[#F6C344] flex-shrink-0">
                  <i className="ri-passport-line text-[#1A365D] text-xl"></i>
                </div>
                <div>
                  <p className="font-bold text-base">Fast Passport Center — Apache Junction, AZ</p>
                  <p className="text-white/60 text-xs mt-0.5">Serving Apache Junction, Gold Canyon &amp; Pinal County</p>
                </div>
              </div>
              <p className="text-white/70 text-sm mb-1">
                <i className="ri-map-pin-line text-[#F6C344] mr-1.5"></i>
                Remote service — all business conducted by mail/overnight courier
              </p>
              <p className="text-white/50 text-xs mb-5 ml-5">No local office needed — start online, ship overnight, we handle the rest</p>
              <div className="border-t border-white/10 pt-5">
                <p className="text-white/80 text-sm leading-relaxed mb-4">
                  After signing up online, ship your documents overnight from any UPS or FedEx location in Apache Junction or Gold Canyon. Most clients receive their passports in 2–9 business days — no driving to the Phoenix Passport Agency required.
                </p>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Start My Passport <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200">
                  <div className="w-9 h-9 flex items-center justify-center rounded-full bg-[#F6C344]/15 mb-3">
                    <i className={`${b.icon} text-[#1A365D] text-base`}></i>
                  </div>
                  <p className="font-bold text-[#1A365D] text-sm mb-1">{b.title}</p>
                  <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <div className="bg-[#F7F8FA] rounded-lg p-7 mb-6 border border-gray-200">
              <h3 className="font-bold text-[#1A365D] text-sm uppercase tracking-wide mb-5">
                <i className="ri-map-pin-2-line text-[#F6C344] mr-2"></i>
                Communities We Serve
              </h3>
              <div className="space-y-1.5">
                {cities.map((city, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between py-2.5 px-3 bg-white rounded-md"
                  >
                    <div className="flex items-center gap-2.5">
                      <i className="ri-checkbox-circle-fill text-[#F6C344] text-sm"></i>
                      <span className="text-[#1A365D] font-medium text-sm">{city.name}</span>
                    </div>
                    {city.zips && (
                      <span className="text-gray-400 text-xs font-mono hidden sm:inline">{city.zips}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-gray-500 text-xs mt-4 leading-relaxed">
                Anywhere in Pinal County and the East Valley — start online, ship overnight, and we handle the rest. No need to drive to Phoenix or Scottsdale for a passport agency appointment.
              </p>
              <div className="mt-4 pt-4 border-t border-gray-100">
                <p className="text-gray-400 text-xs font-semibold uppercase tracking-wider mb-3">Nearby Cities We Serve</p>
                <div className="flex flex-wrap gap-2">
                  {[
                    { name: "Mesa", href: "/arizona" },
                    { name: "Chandler", href: "/arizona" },
                    { name: "Gilbert", href: "/arizona" },
                    { name: "Scottsdale", href: "/arizona" },
                    { name: "Tucson", href: "/arizona" },
                    { name: "Phoenix", href: "/arizona" },
                  ].map((link) => (
                    <a
                      key={link.name}
                      href={link.href}
                      className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-white border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5 whitespace-nowrap"
                    >
                      {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-[#1A365D] rounded-lg p-5">
              <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-4">Why Apache Junction Travelers Choose Fast Passport Center</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: "ri-government-line", value: "Registered Courier", label: "U.S. Department of State" },
                  { icon: "ri-award-line", value: "A+ Rating", label: "Better Business Bureau" },
                  { icon: "ri-calendar-check-line", value: "20+ Years", label: "Serving Travelers Nationwide" },
                  { icon: "ri-customer-service-2-line", value: "Real Humans", label: "Live Support — Not a Bot" },
                ].map((item, i) => (
                  <a
                    key={i}
                    href="https://fastpassportcenter.com"
                    className="flex items-center gap-2.5 bg-white/5 hover:bg-white/10 rounded-md px-3 py-2.5 transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
                      <i className={`${item.icon} text-sm text-[#F6C344]`}></i>
                    </div>
                    <div className="text-left">
                      <span className="text-white text-xs font-bold block leading-tight">{item.value}</span>
                      <span className="text-white/50 text-xs leading-tight block">{item.label}</span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}